// client/src/components/scheduling/ProposalActionModal.js
import React, { useState, useMemo } from 'react'
import { formatUtcToLocal } from '../../utils/format'
import { showSuccess, showError } from '../../utils/notifications'
import {
  PROPOSAL_MESSAGES,
  getFailureMessage,
} from '../../constants/proposalMessages'
import services from '../../services'
import { getHomeAwayStatus } from '../../utils/match'
import MatchConfirmationModal from './MatchConfirmationModal'
import DeleteDayAvailabilityModal from './DeleteDayAvailabilityModal'
import './ProposalActionModal.css'

export default function ProposalActionModal({
  isOpen,
  onClose,
  proposal,
  opponent,
  onAction,
  currentPlayerId, // New prop for home/away determination
  match, // New prop for match data with home/away info
}) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [matchData, setMatchData] = useState(null)

  const formattedTime = useMemo(() => {
    try {
      return formatUtcToLocal(proposal.proposed_time)
    } catch {
      return proposal?.proposed_time
    }
  }, [proposal?.proposed_time])

  if (!isOpen || !proposal || !opponent) return null
  const opponentDisplayName =
    opponent.display_name ||
    [opponent.first_name || '', opponent.last_name || ''].join(' ').trim() ||
    'Unknown Opponent'

  const isReceived = !proposal.is_sent_by_user
  const isSent = proposal.is_sent_by_user

  // Determine home/away status
  const homeAwayStatus = getHomeAwayStatus(match, currentPlayerId)
  const isCurrentPlayerHome = homeAwayStatus.isHome
  const showHomeAwayIndicator = homeAwayStatus.shouldShow && Boolean(match)

  const handleAction = async (action) => {
    if (isSubmitting) return

    setIsSubmitting(true)
    try {
      let result
      switch (action) {
        case 'accept':
          console.log('✅ Accepting proposal:', proposal.id)
          result = await services.matchProposal.acceptProposal(proposal.id)
          console.log('✅ Accept result:', result)

          if (result.success) {
            console.log('✅ Accept proposal result:', result)
            showSuccess(PROPOSAL_MESSAGES.ACCEPTED)

            // Always show confirmation modal after accepting
            const matchData = {
              ...(result.data?.match || {}),
              // Store additional data needed for navigation
              matchDate: proposal.proposed_time,
              divisionId: proposal.division_id,
              league_id: proposal.league_id,
            }
            console.log('📊 Match data for modal:', matchData)
            console.log('📊 showConfirmationModal will be set to:', true)
            console.log('📊 matchData is truthy?:', !!matchData)
            console.log('📊 result.data:', result.data)
            console.log('📊 result.data.match:', result.data?.match)
            setMatchData(matchData)
            setShowConfirmationModal(true)
            return // Don't call onAction/onClose yet
          }
          break
        case 'reject':
          result = await services.matchProposal.rejectProposal(proposal.id)
          if (result.success) {
            showSuccess(PROPOSAL_MESSAGES.REJECTED)
          }
          break
        case 'cancel':
          result = await services.matchProposal.cancelProposal(proposal.id)
          if (result.success) {
            showSuccess(PROPOSAL_MESSAGES.CANCELED)
          }
          break
        case 'dismiss':
          result = await services.matchProposal.dismissProposal(proposal.id)
          if (result.success) {
            showSuccess(PROPOSAL_MESSAGES.DISMISSED)
          }
          break
        default:
          throw new Error(`${PROPOSAL_MESSAGES.UNKNOWN_ACTION}: ${action}`)
      }

      if (result && result.success) {
        onAction(action, proposal)
        onClose()
      } else {
        showError(result?.error || result?.message || getFailureMessage(action))
      }
    } catch (error) {
      console.error(`Error ${action}ing proposal:`, error)
      showError(error?.message || getFailureMessage(action))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleConfirmationClose = () => {
    setShowConfirmationModal(false)
    setMatchData(null)

    // Now call the original callbacks
    onAction('accept', proposal)
    onClose()
  }

  const handleManageAvailability = () => {
    // Close confirmation modal and show availability deletion modal
    setShowConfirmationModal(false)
    setShowDeleteModal(true)
  }

  const handleDeleteModalClose = () => {
    setShowDeleteModal(false)
    setMatchData(null)

    // Now call the original callbacks
    onAction('accept', proposal)
    onClose()
  }

  return (
    <div
      className="proposal-action-modal-overlay"
      onClick={onClose}
      data-testid="proposal-action-modal-overlay"
    >
      <div
        className="proposal-action-modal-content"
        onClick={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        data-testid="proposal-action-modal"
      >
        <div className="proposal-action-modal-header">
          <h3>
            {isReceived ? 'Match Proposal Received' : 'Match Proposal Sent'}
          </h3>
          <button className="proposal-action-close-button" onClick={onClose}>
            ×
          </button>
        </div>

        <div className="proposal-action-modal-body">
          <div className="proposal-details">
            <div className="opponent-info">
              <h4>
                {isReceived ? 'From: ' : 'To: '}
                {opponentDisplayName}
                {showHomeAwayIndicator && (
                  <span
                    className={`home-away-badge ${isCurrentPlayerHome ? 'badge-home' : 'badge-away'}`}
                    style={{ marginLeft: '8px', fontSize: '0.8em' }}
                    title={
                      isCurrentPlayerHome
                        ? 'You will host this match'
                        : 'You will travel to their court'
                    }
                    aria-label={
                      isCurrentPlayerHome ? 'Home match' : 'Away match'
                    }
                  >
                    {isCurrentPlayerHome ? '🏠 Home' : '🏃 Away'}
                  </span>
                )}
              </h4>
            </div>

            <div className="time-info">
              <strong>Proposed Time:</strong>
              <p>{formattedTime}</p>
            </div>

            {proposal.location_name && (
              <div className="location-info">
                <strong>Location:</strong>
                <p>{proposal.location_name}</p>
              </div>
            )}

            {proposal.notes && (
              <div className="proposal-notes">
                <strong>Notes:</strong>
                <p>{proposal.notes}</p>
              </div>
            )}

            <div className="status-info">
              <strong>Status:</strong>
              <span className={`status-badge ${proposal.status}`}>
                {proposal.status === 'pending'
                  ? 'Pending Response'
                  : proposal.status === 'declined'
                    ? 'Rejected'
                    : proposal.status === 'accepted'
                      ? 'Accepted'
                      : proposal.status}
              </span>
            </div>
          </div>
        </div>

        <div className="proposal-action-modal-footer">
          {isReceived && proposal.status === 'pending' ? (
            // Received proposal - show accept/reject
            <>
              <button
                className="btn btn-success"
                onClick={() => handleAction('accept')}
                disabled={isSubmitting}
                data-testid="proposal-action-accept"
              >
                {isSubmitting ? 'Processing...' : 'Accept'}
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleAction('reject')}
                disabled={isSubmitting}
                data-testid="proposal-action-reject"
              >
                {isSubmitting ? 'Processing...' : 'Reject'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Close
              </button>
            </>
          ) : isSent && proposal.status === 'pending' ? (
            // Sent proposal - show cancel with better styling
            <>
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Close
              </button>
              <button
                className="btn btn-danger"
                onClick={() => handleAction('cancel')}
                disabled={isSubmitting}
                data-testid="proposal-action-cancel"
              >
                {isSubmitting ? 'Canceling...' : 'Cancel Proposal'}
              </button>
            </>
          ) : isSent && proposal.status === 'declined' ? (
            // Sent proposal that was declined - show dismiss
            <>
              <button
                className="btn btn-secondary"
                onClick={onClose}
                disabled={isSubmitting}
              >
                Close
              </button>
              <button
                className="btn btn-warning"
                onClick={() => handleAction('dismiss')}
                disabled={isSubmitting}
                data-testid="proposal-action-dismiss"
              >
                {isSubmitting ? 'Dismissing...' : 'Dismiss Notification'}
              </button>
            </>
          ) : (
            // Proposal already responded to - just show close
            <button
              className="btn btn-primary"
              onClick={onClose}
              disabled={isSubmitting}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {/* Match confirmation modal for accepted proposals */}
      {showConfirmationModal && matchData && (
        <MatchConfirmationModal
          isOpen={showConfirmationModal}
          onClose={handleConfirmationClose}
          matchData={matchData}
          onDeleteAvailability={handleManageAvailability}
        />
      )}

      {/* Deletion modal for availability management */}
      {showDeleteModal && matchData && (
        <DeleteDayAvailabilityModal
          isOpen={showDeleteModal}
          onClose={handleDeleteModalClose}
          matchDate={matchData.matchDate}
          leagueId={matchData.league_id}
          divisionId={matchData.divisionId}
          opponentName={matchData.opponent_name}
        />
      )}
    </div>
  )
}
